import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppStoreBadges } from '@/components/AppStoreBadges';
import { BookOpen, Brain, Check, MessageSquare, BookmarkPlus, Repeat, Languages, Loader2, Library, FolderOpen } from 'lucide-react';
import { Footer } from '@/components/Footer';
import { SEO } from '@/components/SEO';
import { OrganizationSchema, SoftwareApplicationSchema } from '@/components/StructuredData';
import { MarketingHeader } from '@/components/MarketingHeader';
import { PhoneMockup } from '@/components/PhoneMockup';
import { TestimonialCard } from '@/components/TestimonialCard';

import { useAuth } from '@/hooks/useAuth';
import { isNative } from '@/lib/platform';
import { getLanguageFlag } from '@/config/languages';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import {
  MarketingCTA,
  MarketingSection,
  FeatureCard,
  StepCard,
  CtaBand,
  MotionReveal,
  MarketingCrosslinks,
} from '@/components/marketing';
import { LANDING_STATS } from '@/data/landing-stats';
import { MARKETING_CROSSLINKS } from '@/data/marketing-crosslinks';
import { MARKETED_STUDY_MODE_COUNT } from '@/data/marketed-study-modes';
import { BreadcrumbListSchema } from '@/components/StructuredData';

const HERO_SLIDES = [
  { src: '/images/product-translate.png', alt: 'Leka app showing the translate screen with a Spanish translation and Save to Words button' },
  { src: '/images/product-vocabulary.png', alt: 'Leka app showing a vocabulary list with saved words organized by language' },
  { src: '/images/product-study.png', alt: 'Leka app showing a flashcard study session with spaced repetition' },
  { src: '/images/product-import.png', alt: 'Leka app showing the import vocabulary screen with ready-to-learn phrase packs' },
];

const TESTIMONIALS = [
  { quote: "I am currently A2 Spanish and this app has been a lifesaver", name: "LuciaDiane", source: "App Store" as const, rating: 5 },
  { quote: "This app is amazing because you can both translate and quiz yourself without having to switch apps", name: "henchu", source: "App Store" as const, rating: 5 },
  { quote: "Utilizo esta app para apuntar mi vocabulario de inglés y solo puedo decir que estoy in love it!! 😍 Es genial!! Necesitaba una app así 🙂🙂🙂", name: "X Bea", source: "App Store" as const, rating: 5 },
  { quote: "My fave part of this app is the translate feature!!!!! Google Translate SUCKS compared to this", name: "Krista In Spain", source: "TikTok" as const },
  { quote: "Leka is perfect! The app is intuitive. My student can save vocab words, hear the pronunciation, and even get example sentences. The best feature is the verb conjugator.", name: "contemporary.mary", source: "App Store" as const, rating: 5 },
  { quote: "Leka Translate is a powerful tool that allows me to accumulate more vocabulary faster than any other app I've used. Very easy to use and the pre-made decks are awesome. Highly recommend!!", name: "Tati in Spain", source: "TikTok" as const },
];

const LANGUAGES = [
  { code: 'spanish', label: 'Spanish' },
  { code: 'french', label: 'French' },
  { code: 'german', label: 'German' },
  { code: 'italian', label: 'Italian' },
  { code: 'portuguese', label: 'Portuguese' },
  { code: 'dutch', label: 'Dutch' },
  { code: 'english', label: 'English' },
  { code: 'catalan', label: 'Catalan' },
];

const STEPS = [
  { icon: MessageSquare, title: 'Translate', description: 'Enter any word or phrase.' },
  { icon: BookmarkPlus, title: 'Save', description: 'Add to vocabulary with one click.' },
  { icon: Repeat, title: 'Study', description: 'Practice at the perfect time.' },
];

const FEATURES = [
  { icon: Languages, title: 'Translate & Save Instantly', description: 'See a word? Translate it. Like it? Save it. No copy-pasting between apps.' },
  { icon: FolderOpen, title: 'Organize Your Learning', description: 'Group words by topic, trip, or goal. Study what matters most to you.' },
  { icon: Brain, title: `${MARKETED_STUDY_MODE_COUNT} Ways to Practice`, description: 'Flashcards, listening, speaking, and more. Each mode reinforces learning differently.' },
  { icon: Library, title: 'Start Learning Today', description: 'Curated phrases and verb tables. No blank slate — start speaking faster.' },
];

const HERO_WORDS = 'Finally Remember Every Word You Learn'.split(' ');

/** RFC-150: sourced stat strip — replaces the unsourced "Words Saved / Retention Rate" pair. */
function SourcedStats() {
  return (
    <MotionReveal as="div" className="container mx-auto px-4 mt-4">
      <ul
        data-testid="landing-stats"
        className="flex flex-wrap justify-center gap-8 md:gap-12 text-center list-none"
      >
        {LANDING_STATS.map((stat) => (
          <li key={stat.label}>
            <div className="font-display text-2xl md:text-3xl font-bold text-primary">{stat.value}</div>
            <div className="text-sm text-muted-foreground">{stat.label}</div>
          </li>
        ))}
      </ul>
      <p
        data-testid="landing-stats-sources"
        className="text-xs text-muted-foreground mt-3 text-center max-w-xl mx-auto"
      >
        Sources: {LANDING_STATS.map((s) => s.source).join(' · ')}
      </p>
    </MotionReveal>
  );
}

function HeroCarousel() {
  // RFC-150: slower autoplay (5s) + thin progress bar under dots.
  const AUTOPLAY_MS = 5000;
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: AUTOPLAY_MS, stopOnInteraction: false, stopOnMouseEnter: true }),
  ]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Bumping `progressKey` re-mounts the progress bar so the CSS animation restarts on each slide.
  const [progressKey, setProgressKey] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setProgressKey((k) => k + 1);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => { emblaApi.off('select', onSelect); };
  }, [emblaApi, onSelect]);

  return (
    <div>
      <PhoneMockup>
        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex">
            {HERO_SLIDES.map((slide, i) => (
              <div key={slide.src} className="min-w-0 shrink-0 grow-0 basis-full">
                <img
                  src={slide.src}
                  alt={slide.alt}
                  className="w-full h-auto"
                  width={883}
                  height={1920}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  decoding={i === 0 ? 'sync' : 'async'}
                  {...(i === 0 ? { fetchpriority: 'high' as const } : {})}
                />
              </div>
            ))}
          </div>
        </div>
      </PhoneMockup>
      <div className="flex justify-center gap-2 mt-3">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            className={`w-2 h-2 rounded-full transition-colors ${i === selectedIndex ? 'bg-primary' : 'bg-muted-foreground/30'}`}
            onClick={() => emblaApi?.scrollTo(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
      {/* Thin progress bar under dots — restarts on each slide change. Hidden under reduced-motion. */}
      <div className="mt-2 mx-auto h-0.5 w-24 overflow-hidden rounded-full bg-muted-foreground/15 motion-reduce:hidden" aria-hidden>
        <div
          key={progressKey}
          className="h-full bg-primary/60 carousel-progress-bar"
          style={{ animationDuration: `${AUTOPLAY_MS}ms` }}
        />
      </div>
    </div>
  );
}

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // RFC-150: hero phone parallax — drift ~40% slower than scroll. Reduced-motion safe.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY * 0.4;
        document.querySelectorAll<HTMLElement>('.hero-parallax').forEach((el) => {
          el.style.transform = `translate3d(0, ${-y}px, 0)`;
        });
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (isNative()) {
      if (loading) return;
      if (user) {
        navigate('/translate', { replace: true });
        return;
      }
      const hasSeenOnboarding = localStorage.getItem('native_onboarding_complete');
      if (hasSeenOnboarding) {
        navigate('/auth', { replace: true });
      } else {
        navigate('/native-onboarding', { replace: true });
      }
      return;
    }
    if (!loading && user) {
      navigate('/translate', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Leka — Remember Every Word You Learn"
        description="Stop forgetting vocabulary. Translate, save, and review words with smart flashcards that adapt to your memory. Free to start."
        canonical="https://lekalearning.com"
        keywords="vocabulary app, remember vocabulary, spaced repetition, smart flashcards, learn Spanish, learn French, learn German, phrase packs, verb conjugations, language learning app"
        ogImage="https://lekalearning.com/og/landing.jpg"
      />
      <OrganizationSchema />
      <SoftwareApplicationSchema />
      <MarketingHeader />

      <main id="main-content">
        {/* Hero — bespoke layout (carousel doubles as Tier-3 hero moment) */}
        <section className="relative isolate container mx-auto px-4 py-16 md:py-24 animate-fade-in">
          <div aria-hidden className="hero-accent-blob pointer-events-none" />
          <div className="relative z-10 max-w-5xl mx-auto flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 text-center lg:text-left">
              {/* RFC-150: App Store chip replaces the bare "5-star rated" line. */}
              <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card text-sm font-medium">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-warm/15 text-foreground">
                  <span aria-hidden="true">★</span> 5.0
                </span>
                <span className="text-muted-foreground">App Store · Education</span>
              </div>
              <h1
                className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight tracking-tight h1-word-fade"
                aria-label="Finally Remember Every Word You Learn"
              >
                {HERO_WORDS.map((word, i) => (
                  <span
                    key={`${word}-${i}`}
                    aria-hidden="true"
                    className={word === 'Every' ? 'text-primary' : undefined}
                    style={{ animationDelay: `${i * 80}ms` }}
                  >
                    {word}
                    {i < HERO_WORDS.length - 1 ? '\u00A0' : ''}
                  </span>
                ))}
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-3 max-w-2xl">
                Translate. Save. Remember. Leka's smart flashcards review vocabulary right before you'd forget — so words you learned this week are still there next year.
              </p>

              {/* RFC-150: language pills as a thin ribbon below the subhead, out of the H1 area. */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-2 mb-6">
                {LANGUAGES.map((lang) => (
                  <span key={lang.code} className="px-3 py-1 bg-secondary rounded-full text-xs font-medium text-secondary-foreground">
                    {getLanguageFlag(lang.code)} {lang.label}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <MarketingCTA
                  to="/auth?signup=true"
                  medium="landing"
                  campaign="hero-primary"
                  variant="hero"
                  size="xl"
                  className="w-full sm:w-auto hero-cta-glow"
                >
                  Start Remembering — It's Free
                </MarketingCTA>
              </div>
            </div>

            <div className="w-64 md:w-72 shrink-0 hero-parallax">
              <HeroCarousel />
            </div>
          </div>
        </section>

        {/* RFC-150: sourced stat strip — every value carries a source rendered as a visible footnote. */}
        <SourcedStats />

        {/* How It Works — RFC-150: stepped reveal connecting line on desktop. */}
        <MarketingSection
          title="How It Works"
          kicker="Translate. Save. Remember."
          density="airy"
        >
          <div className="stepped-reveal grid grid-cols-1 md:grid-cols-3 gap-6 relative">
            {STEPS.map((step, i) => (
              <StepCard
                key={step.title}
                number={i + 1}
                title={
                  <span className="inline-flex items-center gap-2">
                    <step.icon className="w-5 h-5 text-primary" aria-hidden="true" />
                    {step.title}
                  </span>
                }
                description={step.description}
                delayMs={i * 200}
              />
            ))}
          </div>
        </MarketingSection>

        {/* Testimonials — RFC-150: CSS-columns masonry */}
        <MarketingSection title="What Learners Are Saying" density="airy">
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 [&>*]:break-inside-avoid">
            {TESTIMONIALS.map((t) => (
              <TestimonialCard key={t.name} {...t} />
            ))}
          </div>
        </MarketingSection>

        {/* Features Grid */}
        <MarketingSection density="dense">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f, i) => (
              <FeatureCard
                key={f.title}
                icon={f.icon}
                title={f.title}
                description={f.description}
                delayMs={i * 60}
              />
            ))}
          </div>
        </MarketingSection>

        {/* RFC-150: crosslink strip — every page seeds the topical-authority graph. */}
        <MarketingCrosslinks
          kicker="Keep exploring"
          items={MARKETING_CROSSLINKS.landing}
        />

        {/* Closing CTA band — RFC-150: AppStoreBadges relocated here from the hero. */}
        <CtaBand
          title={
            <span className="inline-flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-primary" aria-hidden="true" />
              You're Closer to Fluency Than You Think
            </span>
          }
          description="Join learners who are building lasting vocabulary every day. Your first 200 words are free."
          ctas={
            <>
              <MarketingCTA to="/auth?signup=true" medium="landing" campaign="footer-band" variant="hero" size="lg">
                Get Started Free
              </MarketingCTA>
              <AppStoreBadges height="h-10" className="justify-center" />
            </>
          }
          footnote={
            <ul className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
              {['Free to start', 'No credit card required', 'GDPR compliant', 'Available on iOS & Android'].map((item) => (
                <li key={item} className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-primary" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          }
        />
      </main>

      <Footer />
    </div>
  );
}
